import React, { useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Avatar,
  Card,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Typography,
} from "@mui/material";
import { displayPatientName } from "../../utils/displayPatientName";
import { StyledTextFieldBootstrap } from "../doctor-console/StyledComponents";

const InvoicePatientSearchDialog = ({
  open,
  onClose,
  authToken,
  backendUrl,
  onSelectPatient,
}) => {
  const [patientSearchType, setPatientSearchType] = useState("patientId");
  const [patientSearchQuery, setPatientSearchQuery] = useState("");
  const [patientSearchName, setPatientSearchName] = useState("");
  const [patientSearchGender, setPatientSearchGender] = useState("");
  const [patientSearchDob, setPatientSearchDob] = useState("");
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [isPatientSearching, setIsPatientSearching] = useState(false);

  const resetSearchState = () => {
    setPatientSearchType("patientId");
    setPatientSearchQuery("");
    setPatientSearchName("");
    setPatientSearchGender("");
    setPatientSearchDob("");
    setPatientSearchResults([]);
    setIsPatientSearching(false);
  };

  const handleClose = () => {
    resetSearchState();
    onClose();
  };

  const runPatientSearch = async () => {
    if (!authToken) return;

    let filterCriteria;

    if (patientSearchType === "demographics") {
      if (
        !String(patientSearchName || "").trim() &&
        !String(patientSearchGender || "").trim() &&
        !String(patientSearchDob || "").trim()
      ) {
        toast.error("Please enter at least one demographic field.");
        return;
      }
      filterCriteria = {
        filter_type: "demographics",
        filter_details: {
          name: patientSearchName || "",
          gender: patientSearchGender || "",
          dob: patientSearchDob || "",
        },
        multipleResults: true,
      };
    } else if (patientSearchType === "phoneNo") {
      const q = String(patientSearchQuery || "").trim();
      if (!q) {
        toast.error("Please enter phone number.");
        return;
      }
      filterCriteria = {
        filter_type: "phone_number",
        filter_details: { phone_number: q },
        multipleResults: true,
      };
    } else {
      const q = String(patientSearchQuery || "").trim();
      if (!q) {
        toast.error("Please enter patient id.");
        return;
      }
      filterCriteria = {
        filter_type: "patient_id",
        filter_details: { patient_id: q },
        multipleResults: true,
      };
    }

    try {
      setIsPatientSearching(true);
      const res = await axios.post(
        `${backendUrl}get_filtered_patient_data/`,
        filterCriteria,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );

      if (res?.data?.status === "Success") {
        const found = Array.isArray(res?.data?.patient_details)
          ? res.data.patient_details
          : [];
        setPatientSearchResults(found);
        if (found.length === 0) {
          toast.error("No patient found");
        } else {
          toast.success("Patient Found Successfully");
        }
      } else {
        setPatientSearchResults([]);
        toast.error(res?.data?.message || "No patient found");
      }
    } catch (error) {
      console.error("Patient search failed:", error);
      setPatientSearchResults([]);
      toast.error("Patient search failed");
    } finally {
      setIsPatientSearching(false);
    }
  };

  const handleSelect = (patient) => {
    const resolvedPatientId = patient?.patient_id || patient?.pat_id;
    if (!resolvedPatientId) return;
    onSelectPatient({ ...patient, patient_id: resolvedPatientId });
    handleClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          backgroundColor: "#213360",
          color: "#FFFFFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span>Search Patient</span>
        <IconButton onClick={handleClose}>
          <span style={{ color: "#FFFFFF", fontSize: 22 }}>x</span>
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <label className="required-label">Search By</label>
            <StyledTextFieldBootstrap
              fullWidth
              select
              size="small"
              value={patientSearchType}
              onChange={(e) => {
                setPatientSearchType(e.target.value);
                setPatientSearchResults([]);
              }}
            >
              <MenuItem value="patientId">Patient ID</MenuItem>
              <MenuItem value="phoneNo">Phone No</MenuItem>
              <MenuItem value="demographics">Demographics</MenuItem>
            </StyledTextFieldBootstrap>
          </Grid>
          <Grid item xs={12} sm={6}>
            <label>Enter Search Query</label>
            <StyledTextFieldBootstrap
              fullWidth
              size="small"
              placeholder="Enter your search query"
              value={patientSearchQuery}
              onChange={(e) => setPatientSearchQuery(e.target.value)}
              disabled={patientSearchType === "demographics"}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <label>Name</label>
            <StyledTextFieldBootstrap
              fullWidth
              size="small"
              placeholder="Enter patient name"
              value={patientSearchName}
              onChange={(e) => setPatientSearchName(e.target.value)}
              disabled={patientSearchType !== "demographics"}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <label>Gender</label>
            <StyledTextFieldBootstrap
              fullWidth
              size="small"
              select
              value={patientSearchGender}
              onChange={(e) => setPatientSearchGender(e.target.value)}
              disabled={patientSearchType !== "demographics"}
            >
              <MenuItem value="">Select Gender</MenuItem>
              <MenuItem value="M">Male</MenuItem>
              <MenuItem value="F">Female</MenuItem>
              <MenuItem value="O">Other</MenuItem>
            </StyledTextFieldBootstrap>
          </Grid>
          <Grid item xs={12} sm={4}>
            <label>Date of Birth</label>
            <StyledTextFieldBootstrap
              fullWidth
              size="small"
              type="date"
              value={patientSearchDob}
              onChange={(e) => setPatientSearchDob(e.target.value)}
              disabled={patientSearchType !== "demographics"}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
        <div style={{ marginTop: 16 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
            Results
          </Typography>
          {isPatientSearching ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <CircularProgress size={20} />
              <span>Searching...</span>
            </div>
          ) : patientSearchResults && patientSearchResults.length > 0 ? (
            <Grid container spacing={2}>
              {patientSearchResults.map((p) => {
                const patientId = p?.patient_id || p?.pat_id || "-";
                const phoneVal = p.phone_no || p.phone_number || "-";
                return (
                  <Grid item xs={12} sm={6} key={patientId}>
                    <Card
                      onClick={() => handleSelect(p)}
                      sx={{
                        cursor: "pointer",
                        border: "1px solid #E3EFF0",
                        borderRadius: "10px",
                      }}
                    >
                      <Card style={{ boxShadow: "none" }}>
                        <Grid container spacing={0} sx={{ p: 1 }}>
                          <Grid item sx={{ pr: 1 }}>
                            <Avatar sx={{ bgcolor: "#213360" }}>
                              {String(p?.name || "P")
                                .slice(0, 1)
                                .toUpperCase()}
                            </Avatar>
                          </Grid>
                          <Grid item xs>
                            <Typography sx={{ fontWeight: 700 }}>
                              {displayPatientName(p?.name, "-")}
                            </Typography>
                            <Typography variant="body2">
                              Patient ID: {patientId}
                            </Typography>
                            <Typography variant="body2">
                              Phone: {phoneVal}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Card>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Typography variant="body2" sx={{ color: "#6B7A90" }}>
              No results to show.
            </Typography>
          )}
        </div>
      </DialogContent>
      <div
        style={{
          padding: "16px 24px",
          display: "flex",
          gap: "10px",
          width: "100%",
        }}
      >
        <button
          type="button"
          className="btn btn__secondary btn__block"
          onClick={handleClose}
        >
          CLOSE
        </button>
        <button
          type="button"
          className="btn btn__secondary btn__block"
          onClick={runPatientSearch}
          disabled={isPatientSearching}
        >
          {isPatientSearching ? "SEARCHING..." : "SEARCH"}
        </button>
      </div>
    </Dialog>
  );
};

export default InvoicePatientSearchDialog;
